'use client'

import {JourneyActorType, JourneyMeasurement, JourneyMetricOwner, JourneyOutcome, sanitizeJourneyStep} from './journey-contract'

const CORRELATION_COOKIE = 'fitro_correlation_id'
const JOURNEY_ENDPOINT = '/api/logs/journey'

export type JourneyContext = {scope: 'pending' | 'studio'; scopeKey: string | null; actorType: JourneyActorType; partnerId: string | null; partnerName: string | null}
export type JourneyStepInput = {feature: string; action: string; stage: string; outcome: JourneyOutcome; measurement: JourneyMeasurement; metricOwner: JourneyMetricOwner; reasonCode?: string; route?: string; context?: Record<string, string | number | boolean>; entityType?: string; entityId?: string}

let current: JourneyContext = {scope: 'pending', scopeKey: null, actorType: 'customer', partnerId: null, partnerName: null}
let activeStudioScopeKey: string | null = null
type PendingStep = {scopeKey: string; eventId: string; occurredAt: string; order: number; input: JourneyStepInput}
const pendingSteps: PendingStep[] = []
let pendingOrder = 0

const cookie = (name: string): string | null => {
    if (typeof document === 'undefined') return null
    const prefix = `${encodeURIComponent(name)}=`
    for (const part of document.cookie.split(';')) { const value = part.trim(); if (value.startsWith(prefix)) return decodeURIComponent(value.slice(prefix.length)) || null }
    return null
}

const send = (payload: unknown): void => {
    const body = JSON.stringify(payload)
    try {
        if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function' && navigator.sendBeacon(JOURNEY_ENDPOINT, new Blob([body], {type: 'application/json'}))) return
        if (typeof fetch === 'function') void fetch(JOURNEY_ENDPOINT, {method: 'POST', headers: {'content-type': 'application/json'}, body, keepalive: true, credentials: 'same-origin'}).catch(() => undefined)
    } catch { /* telemetry is non-blocking */ }
}

const sendTrusted = (input: JourneyStepInput, eventId: string, occurredAt: string): void => {
    if (current.scope !== 'studio' || !current.scopeKey || current.scopeKey !== activeStudioScopeKey || !current.partnerId || !current.partnerName) return
    const correlationId = cookie(CORRELATION_COOKIE); if (!correlationId) return
    const {partnerId: _partnerId, partnerName: _partnerName, actorType: _actorType, actorId: _actorId, source: _source, app: _app, ...step} = input as JourneyStepInput & Record<string, unknown>
    const payload = sanitizeJourneyStep({event: 'journey.step', schemaVersion: 1, eventId, occurredAt, ...step, correlationId, source: 'browser', app: 'customer', actorType: current.actorType, partnerId: current.partnerId, partnerName: current.partnerName})
    send(payload)
}
const flushPending = (): void => {
    if (current.scope !== 'studio' || !current.scopeKey || current.scopeKey !== activeStudioScopeKey) return
    const matching = pendingSteps.filter(step => step.scopeKey === current.scopeKey).sort((a, b) => a.order - b.order)
    for (let index = pendingSteps.length - 1; index >= 0; index -= 1) if (pendingSteps[index].scopeKey === current.scopeKey) pendingSteps.splice(index, 1)
    for (const step of matching) {
        try { sendTrusted(step.input, step.eventId, step.occurredAt) } catch { /* invalid queued metadata is dropped */ }
    }
}
export const setJourneyContext = (context: JourneyContext): void => { current = context; try { flushPending() } catch { /* telemetry must not affect context updates */ } }
export const setJourneyActiveStudioScope = (scopeKey: string | null): void => {
    activeStudioScopeKey = scopeKey
    for (let index = pendingSteps.length - 1; index >= 0; index -= 1) if (pendingSteps[index].scopeKey !== scopeKey) pendingSteps.splice(index, 1)
}
export const resetJourneyContext = (): void => { activeStudioScopeKey = null; current = {scope: 'pending', scopeKey: null, actorType: 'customer', partnerId: null, partnerName: null}; pendingSteps.splice(0); pendingOrder = 0 }

export const trackJourneyStep = (input: JourneyStepInput): void => {
    try {
        if (typeof window === 'undefined' || typeof crypto?.randomUUID !== 'function') return
        const eventId = crypto.randomUUID()
        const occurredAt = new Date().toISOString()
        if (current.scope === 'pending' && activeStudioScopeKey) {
            if (pendingSteps.length >= 32) pendingSteps.shift()
            pendingSteps.push({scopeKey: activeStudioScopeKey, eventId, occurredAt, order: pendingOrder++, input})
            return
        }
        sendTrusted(input, eventId, occurredAt)
    } catch { /* telemetry is non-blocking */ }
}
